import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import LocateAddressSearch from './LocateAddressSearch';
import {
  insetPlan,
  polygonArea,
  polygonPerimeter,
  toPlanVertices,
} from '../../utils/plotGeom';
import {
  formatFt,
  ftToM,
  m2ToSqFt,
  mToFt,
  SNAP_FT_OPTIONS,
  snapFtMatches,
} from '../../utils/unitsUs';

function Readout({ k, v, u }) {
  return (
    <div className="readout">
      <div className="k">{k}</div>
      <div className="v">
        {v}
        {u ? <span className="u">{u}</span> : null}
      </div>
    </div>
  );
}

export default function LocateSidePanel({
  address,
  onAddressChange,
  onLoadSite,
  siteLoading,
  siteError,
  /** Soft Photorealistic ladder note — docs/PHOTOREALISTIC_MOBILE_FALLBACK.md */
  contextNote,
  site,
  lot,
  setback,
  onSetbackChange,
  northDeg,
  onNorthChange,
  snap,
  onSnapChange,
  selectedVertexIndex,
  onSelectVertex,
  onApplyPreset,
  onResetLotFloor,
  floorY,
  showAddressSearch = true,
}) {
  const planVerts = useMemo(() => toPlanVertices(lot?.vertices || []), [lot?.vertices]);
  const inset = useMemo(() => insetPlan(planVerts, setback), [planVerts, setback]);
  const area = useMemo(() => polygonArea(planVerts), [planVerts]);
  const buildArea = useMemo(() => polygonArea(inset), [inset]);
  const perimeter = useMemo(() => polygonPerimeter(planVerts), [planVerts]);

  const floorReady = floorY != null && Number.isFinite(floorY);

  return (
    <aside className="side">
      <div className="side-head">
        <div className="side-h">step 01 · locate</div>
        <div className="side-title">Plot &amp; site</div>
        <div className="side-sub">
          Search an address on the map, then refine the property line and setbacks.
          Vertices snap to a {formatFt(snap.size, 1)} grid.
        </div>
      </div>

      <div className="side-body">
        <div className="side-section">
          <div className="sec-h">site</div>
          {showAddressSearch && (
            <div className="field">
              <div className="field-label">
                <span>address</span>
              </div>
              <LocateAddressSearch
                address={address}
                onAddressChange={onAddressChange}
                onLoadSite={onLoadSite}
                siteLoading={siteLoading}
                siteError={siteError}
                variant="panel"
              />
            </div>
          )}
          {!showAddressSearch && siteError && (
            <div className="field-error">{siteError}</div>
          )}
          {contextNote && (
            <div
              className="field-label"
              style={{
                marginTop: 8,
                textTransform: 'none',
                letterSpacing: 0,
                color: 'var(--ink-2)',
                fontSize: 11,
                lineHeight: 1.35,
              }}
              role="status"
            >
              {contextNote}
            </div>
          )}
          {site?.formattedAddress && (
            <div
              className="field-label"
              style={{ marginTop: showAddressSearch ? 8 : 0, textTransform: 'none', letterSpacing: 0 }}
            >
              <span className="mono" style={{ color: 'var(--ink-2)', fontSize: 11 }}>
                {site.formattedAddress}
                  {floorReady ? ` · floor ${formatFt(floorY)}` : ''}
              </span>
            </div>
          )}
          {site && (
            <button
              type="button"
              className="button-secondary"
              style={{ marginTop: 8 }}
              onClick={onResetLotFloor}
            >
              Reset to floor
            </button>
          )}
        </div>

        <div className="side-section">
          <div className="readouts">
            <Readout k="plot area" v={m2ToSqFt(area).toFixed(0)} u="sq ft" />
            <Readout k="build envelope" v={m2ToSqFt(buildArea).toFixed(0)} u="sq ft" />
            <Readout k="vertices" v={planVerts.length} u="" />
            <Readout k="perimeter" v={mToFt(perimeter).toFixed(1)} u="ft" />
          </div>
        </div>

        <div className="side-section">
          <div className="sec-h">setback</div>
          <div className="field">
            <div className="field-label">
              <span>boundary offset</span>
              <span className="v">{formatFt(setback)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="20"
              step="0.5"
              value={mToFt(setback)}
              onChange={(e) => onSetbackChange(ftToM(parseFloat(e.target.value)))}
            />
          </div>
          <div className="field">
            <div className="field-label">
              <span>north heading</span>
              <span className="v">{northDeg}°</span>
            </div>
            <input
              type="range"
              min="0"
              max="360"
              step="5"
              value={northDeg}
              onChange={(e) => onNorthChange(parseInt(e.target.value, 10))}
            />
          </div>
          <div className="field">
            <div className="field-label">
              <span>snap</span>
              <span className="v">
                {formatFt(snap.size, snapFtMatches(snap.size, 0.5) ? 1 : 0)}{' '}
                {snap.enabled ? 'on' : 'off'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SNAP_FT_OPTIONS.map((ft) => (
                <button
                  key={ft}
                  type="button"
                  className={`chip${snap.enabled && snapFtMatches(snap.size, ft) ? ' sel' : ''}`}
                  onClick={() => onSnapChange({ size: ftToM(ft), enabled: true })}
                >
                  {ft} ft
                </button>
              ))}
              <button
                type="button"
                className={`chip${!snap.enabled ? ' warn' : ''}`}
                onClick={() => onSnapChange({ enabled: !snap.enabled })}
              >
                {snap.enabled ? 'on' : 'off'}
              </button>
            </div>
          </div>
        </div>

        <div className="side-section">
          <div className="sec-h">vertices</div>
          <div>
            {planVerts.map((v) => (
              <div
                key={v.id}
                className={`list-row${selectedVertexIndex === v.lotIndex ? ' sel' : ''}`}
                onClick={() => onSelectVertex(v.lotIndex)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelectVertex(v.lotIndex);
                }}
              >
                <span className="swatch" />
                <span>v{v.lotIndex + 1}</span>
                <span className="meta">
                  {mToFt(v.x).toFixed(1)}, {mToFt(v.y).toFixed(1)} ft
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="side-section">
          <div className="sec-h">presets</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              type="button"
              className="button-secondary"
              onClick={() => onApplyPreset('rectangle')}
            >
              rectangle 79×52 ft
            </button>
            <button
              type="button"
              className="button-secondary"
              onClick={() => onApplyPreset('lshape')}
            >
              L-shape
            </button>
            <button
              type="button"
              className="button-secondary"
              onClick={() => onApplyPreset('trapezoid')}
            >
              trapezoid
            </button>
          </div>
        </div>

        <div className="side-section" style={{ borderBottom: 0 }}>
          <Link className="button-primary" to="/xlabs/design">
            continue → design
          </Link>
        </div>
      </div>
    </aside>
  );
}
